import React, { useState } from 'react'
import { Button, Separator, Card, Paragraph, H2, H5, LayoutBand } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import DesktopPushNotificationInstructions from './DesktopPushNotificationInstructions'
import { requestBrazePushPermission } from '../../helpers/brazePush'
import { usePWAMode } from '../../hooks/appRootHooks'
import getIsTWAMode from '../../helpers/appMode_helper'

const PermissionGranted = ({ t }) => (
  <>
    <Separator />
    <Card>
      <H2 size="H5">{t('invitations.notification-preference.permission-granted', 'Permission Granted')}</H2>
      <Paragraph>
        {t(
          'invitations.notification-preference.permission-granted-explanation',
          'You have given permission for your device to receive push notifications from family group members.*'
        )}
      </Paragraph>
    </Card>
  </>
)

const PermissionDenied = ({ t }) => (
  <Card data-testid="denied-card">
    <H5>{t('invitations.notification-preference.permission-denied', "You've denied permission*")}</H5>
    <Separator />
  </Card>
)

const PermissionDefault = ({ t, handleClick }) => (
  <>
    <Button emphasis="high" onClick={handleClick}>
      {t('invitations.get-push-notifications.action', 'Get Push Notifications')}
    </Button>
    <Separator />
    <Paragraph>
      {t(
        'invitations.notification-preference.request-permission-explanation',
        'Grant permission for us to send you notifications to your device from family group members.*'
      )}
    </Paragraph>
  </>
)

const PWAOverrideExplanation = ({ t }) => (
  <div data-testid="pwa-override-explanation">
    <Separator />
    <Paragraph>
      {t(
        'invitations.notification-preference.permission-override-explanation',
        'You can override or change the push notifications settings by long-pressing on the Together By FamilySearch application icon, selecting "Site Settings" and adjusting notifications permissions there.  Restart Together By FamilySearch to use the updated settings.*'
      )}
    </Paragraph>
  </div>
)

const TWAExplanation = ({ t }) => (
  <>
    <Separator />
    <Paragraph>
      {t(
        'invitations.notification-preference.twa-explanation',
        'Control the push notifications settings by long-pressing on the Together By FamilySearch application icon, selecting "Site Settings" and adjusting notifications permissions there. Restart Together By FamilySearch to use the updated settings.*'
      )}
    </Paragraph>
  </>
)

export default function PushNotificationManager() {
  const { t } = useTranslation()

  const permission = window?.Notification?.permission ?? ''
  const [currentPermissionState, setCurrentPermissionState] = useState(permission)
  const isPWA = usePWAMode()
  const isTWA = getIsTWAMode()

  const [isRequesting, setIsRequesting] = useState(false)

  const handleClick = async () => {
    if (isRequesting) return
    setIsRequesting(true)
    try {
      const requestedPermissionResult = await requestBrazePushPermission()
      setCurrentPermissionState(requestedPermissionResult)
    } catch (e) {
      // eslint-disable-next-line no-console -- fallback logging for unexpected errors
      console.error('Error requesting push permission', e)
    } finally {
      setIsRequesting(false)
    }
  }

  return (
    <LayoutBand>
      <Separator size="lg" />
      {currentPermissionState === 'default' && (
        <div aria-live="polite">
          <PermissionDefault t={t} handleClick={handleClick} />
          {isRequesting && (
            <Paragraph aria-label={t('common-ui:loading.ellipsis.label', 'Loading...')}>
              {t('invitations.notification-preference.requesting-permission', 'Requesting permission...')}
            </Paragraph>
          )}
        </div>
      )}
      {currentPermissionState === 'granted' && <PermissionGranted t={t} />}
      {currentPermissionState === 'denied' && <PermissionDenied t={t} />}
      {currentPermissionState !== 'default' && isPWA && !isTWA && <PWAOverrideExplanation t={t} />}
      {currentPermissionState !== 'default' && !isPWA && !isTWA && <DesktopPushNotificationInstructions />}
      {isTWA && <TWAExplanation t={t} />}
      <Separator size="xl" />
    </LayoutBand>
  )
}
